import { useState, useMemo } from "react";

// Constantes normativas 2026 (Perú)
const RMV_2026 = 1130;
const ASIGNACION_FAMILIAR = RMV_2026 * 0.10; // 10% de la RMV

const MODOS = [
  { id: "completas", etiqueta: "Completas", icono: "🏖️" },
  { id: "truncas", etiqueta: "Truncas", icono: "📆" },
  { id: "no_gozadas", etiqueta: "No gozadas (triple)", icono: "⚠️" },
  { id: "venta", etiqueta: "Venta de días", icono: "💵" },
];

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function CalculadoraVacaciones() {
  const [modo, setModo] = useState("completas");
  const [sueldoBruto, setSueldoBruto] = useState(3000);
  const [tieneHijos, setTieneHijos] = useState(false);

  // Truncas
  const [mesesCompletos, setMesesCompletos] = useState(6);
  const [diasSueltos, setDiasSueltos] = useState(0);

  // Venta de vacaciones
  const [diasVendidos, setDiasVendidos] = useState(15);

  const resultado = useMemo(() => {
    const bruto = Number(sueldoBruto) || 0;
    const asignacion = tieneHijos ? ASIGNACION_FAMILIAR : 0;
    const remuneracionComputable = bruto + asignacion;
    const remuneracionDiaria = remuneracionComputable / 30;

    if (modo === "completas") {
      // Récord vacacional cumplido: 30 días de descanso = 1 remuneración completa
      return {
        remuneracionComputable,
        total: remuneracionComputable,
        detalle: [{ etiqueta: "Remuneración vacacional (30 días)", monto: remuneracionComputable }],
      };
    }

    if (modo === "truncas") {
      const meses = Math.min(11, Math.max(0, Number(mesesCompletos) || 0));
      const dias = Math.max(0, Number(diasSueltos) || 0);
      const diasComputables = Math.min(360, meses * 30 + dias);
      const total = remuneracionComputable * (diasComputables / 360);
      return {
        remuneracionComputable,
        diasComputables,
        total,
        detalle: [
          { etiqueta: `Tiempo computable: ${diasComputables} / 360 días`, monto: total },
        ],
      };
    }

    if (modo === "no_gozadas") {
      // Récord cumplido pero no se otorgó descanso dentro de los 12 meses siguientes.
      // Se deben pagar 3 remuneraciones: (1) el trabajo ya realizado -no es un pago extra-,
      // (2) la remuneración vacacional adquirida y no gozada, (3) una indemnización adicional.
      const vacacionalNoGozada = remuneracionComputable;
      const indemnizacion = remuneracionComputable;
      const total = vacacionalNoGozada + indemnizacion;
      return {
        remuneracionComputable,
        total,
        detalle: [
          { etiqueta: "Remuneración vacacional no gozada", monto: vacacionalNoGozada },
          { etiqueta: "Indemnización (no afecta a descuentos)", monto: indemnizacion },
        ],
      };
    }

    // venta
    const dias = Math.min(15, Math.max(0, Number(diasVendidos) || 0));
    const total = remuneracionDiaria * dias;
    return {
      remuneracionComputable,
      remuneracionDiaria,
      total,
      detalle: [
        { etiqueta: `Pago por ${dias} día(s) vendido(s)`, monto: total },
      ],
    };
  }, [modo, sueldoBruto, tieneHijos, mesesCompletos, diasSueltos, diasVendidos]);

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            🏖️
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora de Vacaciones
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · Completas, truncas, no gozadas y venta</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-1.5 mb-4">
          {MODOS.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setModo(m.id)}
              className={`rounded-lg border py-2.5 px-1 text-xs font-medium transition-colors flex flex-col items-center gap-1 ${
                modo === m.id
                  ? "border-red-700 bg-red-50 text-red-800"
                  : "border-slate-300 text-slate-600 hover:bg-slate-50 bg-white"
              }`}
            >
              <span className="text-base">{m.icono}</span>
              {m.etiqueta}
            </button>
          ))}
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Sueldo bruto mensual
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                  S/
                </span>
                <input
                  type="number"
                  min="0"
                  value={sueldoBruto}
                  onChange={(e) => setSueldoBruto(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-4 py-2.5 text-slate-900 font-mono text-lg focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                  placeholder="3000"
                />
              </div>
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={tieneHijos}
                onChange={(e) => setTieneHijos(e.target.checked)}
                className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
              />
              <span className="text-sm text-slate-700">
                Recibo asignación familiar ({formatearSoles(ASIGNACION_FAMILIAR)})
              </span>
            </label>

            {modo === "completas" && (
              <p className="p-3 text-sm rounded-lg text-slate-500 bg-slate-50">
                Aplica cuando ya cumpliste el récord vacacional (1 año de servicio y
                asistencia mínima requerida) y vas a gozar los 30 días completos.
              </p>
            )}

            {modo === "truncas" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Meses completos trabajados
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="11"
                    value={mesesCompletos}
                    onChange={(e) => setMesesCompletos(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                  />
                  <p className="mt-1 text-xs text-slate-400">de 0 a 11</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Días sueltos
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="29"
                    value={diasSueltos}
                    onChange={(e) => setDiasSueltos(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                  />
                  <p className="mt-1 text-xs text-slate-400">del mes incompleto</p>
                </div>
              </div>
            )}

            {modo === "no_gozadas" && (
              <p className="p-3 text-sm rounded-lg text-slate-500 bg-amber-50">
                Aplica cuando ya cumpliste el récord vacacional pero el empleador no te
                otorgó el descanso dentro de los 12 meses siguientes. Se calcula sobre
                una remuneración completa; si tu año no está completo, usa el modo
                "Truncas" para la parte proporcional.
              </p>
            )}

            {modo === "venta" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Días a vender (máximo 15)
                </label>
                <input
                  type="number"
                  min="0"
                  max="15"
                  value={diasVendidos}
                  onChange={(e) => setDiasVendidos(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                />
                <p className="mt-1 text-xs text-slate-400">
                  Por ley solo puedes vender hasta 15 de los 30 días, mediante acuerdo
                  por escrito. Los otros 15 son de descanso obligatorio.
                </p>
              </div>
            )}
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              {modo === "no_gozadas" ? "Pago adicional estimado" : "Monto estimado"}
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(Math.max(0, resultado.total))}
            </p>

            <div className="space-y-1.5 text-sm">
              {resultado.detalle.map((item, i) => (
                <div key={i} className="flex justify-between text-slate-300">
                  <span>{item.etiqueta}</span>
                  <span className="font-mono">{formatearSoles(item.monto)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-slate-400">
          Herramienta orientativa. El descanso vacacional requiere haber cumplido el
          récord de asistencia establecido por ley (D. Leg. 713). En el caso de "no
          gozadas", el pago adicional (vacacional no gozada + indemnización) no
          incluye la remuneración por el trabajo ya realizado durante ese periodo, que
          se paga de forma regular. No constituye asesoría legal, contable ni laboral.
        </p>
      </div>
    </div>
  );
}